import { useState, useRef, useEffect } from "react";
import { t } from "../i18n.js";
import { ensureCtx, out } from "../audio.js";
import { submitPusherScore, fetchPusherBoard } from "../pusherboard.js";

/* ==================== COIN PUSHER – to lag, ren timing ====================
   Spilleren får 100 Teddy-SØLVMØNTER og skal presse GULDMØNTER ud af
   maskinen. To platforme med hver sin oscillerende pusher: mønten slippes
   fra den vandrende dispenser-slids øverst — TIMINGEN afgør, hvor dybt den
   lander bag pusheren (retrakteret pusher = fuldt skub hver cyklus; en
   mønt, der lander foran den udstrakte pusher, må vente på kædepres).
   Øverste forkant drysser ned på nederste lag; nederste forkant drysser i
   bakken: sølv ryger TILBAGE i beholdningen (ekstra skud), guld tæller på
   scoren. Bakken har sidegruber: mønter yderst er TABT. Spillet slutter,
   når beholdningen er tom og maskinen står stille; guld-scoren indsendes
   til det dobbelte high score (pusherboard.js, samme mønster som sækken).
========================================================================== */

const W = 360, H = 540;
const CX = 180;
const AMMO_START = 100;
/* platform-geometri: y0=bagkant, y1=forkant (skærm), halvbredder m. perspektiv */
const UP = { y0: 150, y1: 248, backHW: 100, frontHW: 122 };
const LO = { y0: 256, y1: 386, backHW: 122, frontHW: 145 };
const R = 12;                         // møntradius (x-retning)
const TRAY_HW = 92;                   // vinderzonens halvbredde ved forkanten
const halfW = (L, z) => L.backHW + (L.frontHW - L.backHW) * z;
const yAt = (L, z) => L.y0 + (L.y1 - L.y0) * z;
const rz = (L) => R / (L.y1 - L.y0);  // radius i z-enheder

function makeAudio() {
  let ctx = null;
  const ensure = () => (ctx = ensureCtx());
  const tone = (f, t0, dur, type = "sine", vol = 0.12) => {
    const c = ensure(); if (!c) return;
    const o = c.createOscillator(), g = c.createGain();
    o.type = type; o.frequency.setValueAtTime(f, t0);
    g.gain.setValueAtTime(vol, t0);
    g.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
    o.connect(g); g.connect(out()); o.start(t0); o.stop(t0 + dur);
  };
  return {
    unlock: ensure,
    drop() { const c = ensure(); if (c) tone(700, c.currentTime, 0.05, "square", 0.07); },
    thunk() { const c = ensure(); if (!c) return; tone(160, c.currentTime, 0.1, "square", 0.09); tone(90, c.currentTime + 0.01, 0.14, "sine", 0.1); },
    silver() { const c = ensure(); if (!c) return; tone(880, c.currentTime, 0.09, "triangle", 0.1); tone(1180, c.currentTime + 0.06, 0.1, "triangle", 0.08); },
    gold() { const c = ensure(); if (!c) return; [880, 1100, 1320, 1760].forEach((f, i) => tone(f, c.currentTime + i * 0.07, 0.18, "triangle", 0.12)); },
    lost() { const c = ensure(); if (c) tone(200, c.currentTime, 0.16, "sawtooth", 0.06); },
    rain() { const c = ensure(); if (!c) return; [1320, 1100, 1560, 1320, 1760].forEach((f, i) => tone(f, c.currentTime + i * 0.08, 0.2, "triangle", 0.1)); },
    fanfare() { const c = ensure(); if (!c) return; [523, 659, 784, 1046, 1318].forEach((f, i) => tone(f, c.currentTime + i * 0.11, 0.28, "triangle", 0.13)); },
  };
}

export default function Pusher({ onExit }) {
  const [phase, setPhase] = useState("setup");   // setup | playing | result
  const [result, setResult] = useState(null);    // {gold}
  const [hud, setHud] = useState({ ammo: AMMO_START, gold: 0 });
  /* dobbelt high score-overlay (samme mønster som Slå sækken) */
  const [hsOpen, setHsOpen] = useState(false);
  const [hsTab, setHsTab] = useState("month");
  const [hsData, setHsData] = useState(null);
  const [hsErr, setHsErr] = useState(false);
  const openHs = () => {
    setHsOpen(true);
    setHsErr(false);
    setHsData(null);
    fetchPusherBoard().then(setHsData).catch(() => setHsErr(true));
  };
  const fmtDate = (ts, withYear) => {
    let lang;
    try { lang = localStorage.getItem("carnival.lang") || undefined; } catch {}
    try {
      return new Date(ts).toLocaleDateString(lang, {
        day: "numeric", month: "short", ...(withYear ? { year: "2-digit" } : {}),
      });
    } catch { return ""; }
  };
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const audioRef = useRef(null);
  if (!audioRef.current) audioRef.current = makeAudio();

  const startGame = () => {
    setResult(null);
    setHud({ ammo: AMMO_START, gold: 0 });
    setPhase("playing");
    audioRef.current.unlock();
  };

  /* ================= game loop ================= */
  useEffect(() => {
    if (phase !== "playing") return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const g = {
      t: 0,
      ammo: AMMO_START,
      gold: 0,
      up: [],      // mønter på øverste platform {x, z, gold}
      low: [],     // nederste
      falls: [],   // faldende mønter {x, y0, y1, t, dur, gold, to: "low"|"tray"|"lost"}
      fx: [],      // svævende +1-tekster
      banner: null,   // {txt, t} — GULDREGN-besked
      refillCd: 0,    // cooldown på tærskel-guldregn
      dropCount: 0,   // spillerens drops (hver 25. udløser bonus-guldregn)
      quiet: 0,
      over: false,
    };
    const p1 = () => 0.03 + 0.145 * (1 + Math.sin(g.t * (Math.PI * 2 / 2.8)));
    const p2 = () => 0.03 + 0.125 * (1 + Math.sin(g.t * (Math.PI * 2 / 3.7) + 2.1));
    const slotX = () => CX + Math.sin(g.t * 1.15) * 95;

    /* startfyld: guldet ER i maskinen (spilleren skyder kun sølv ind) */
    const fill = (L, list, n, goldP) => {
      for (let i = 0; i < n; i++) {
        list.push({
          x: CX + (Math.random() * 2 - 1) * (halfW(L, 0.6) - R - 2),
          z: 0.34 + Math.random() * 0.58,
          gold: Math.random() < goldP,
        });
      }
    };
    /* mønt-TÅRNE som i en rigtig pusher: stakke på 3-5 mønter (guld-tunge,
       lag-liste bund→top) — vælter ud i enkeltmønter, når de ryger over kanten */
    const fillTowers = (L, list, n) => {
      for (let i = 0; i < n; i++) {
        const layers = Array.from({ length: 3 + Math.floor(Math.random() * 3) },
          () => Math.random() < 0.6);
        list.push({
          x: CX + (Math.random() * 2 - 1) * (halfW(L, 0.55) - R - 6),
          z: 0.4 + Math.random() * 0.45,
          tower: layers,
        });
      }
    };
    fill(UP, g.up, 18, 0.18);
    fill(LO, g.low, 27, 0.20);
    fillTowers(UP, g.up, 2);
    fillTowers(LO, g.low, 3);
    for (let i = 0; i < 12; i++) relax(UP, g.up, true) + relax(LO, g.low, true);

    /* hvor meget guld er der tilbage i maskinen (plader + tårne + i luften)? */
    const goldInMachine = () => {
      let n = 0;
      for (const c of [...g.up, ...g.low]) n += c.tower ? c.tower.filter(Boolean).length : (c.gold ? 1 : 0);
      for (const f of g.falls) if (f.gold && f.to !== "tray" && f.to !== "lost") n += 1;
      return n;
    };
    /* GULDREGN: dispenseren drysser selv guld i puljen (banner + klokkespil) */
    const goldRain = (n) => {
      for (let i = 0; i < n; i++) {
        g.falls.push({
          x: 70 + Math.random() * (W - 140),
          y0: 96, y1: 0, t: -0.18 * i, dur: 0.34, gold: true, to: "up",
        });
      }
      g.banner = { txt: t("pu_goldrain"), t: 0 };
      g.refillCd = 9;
      audioRef.current.rain();
    };

    function relax(L, list, init) {
      const dp = L.y1 - L.y0;
      for (let a = 0; a < list.length; a++) {
        for (let b = a + 1; b < list.length; b++) {
          const A = list[a], B = list[b];
          const dx = B.x - A.x, dzp = (B.z - A.z) * dp;
          const d = Math.hypot(dx, dzp);
          if (d > 0.001 && d < R * 2) {
            const push = (R * 2 - d) / 2;
            const nx = dx / d, nz = dzp / d / dp;
            A.x -= nx * push; A.z -= nz * push;
            B.x += nx * push; B.z += nz * push;
          }
        }
      }
      for (const c of list) {
        const hw = halfW(L, c.z) - R;
        if (c.x < CX - hw) c.x = CX - hw;
        if (c.x > CX + hw) c.x = CX + hw;
        if (init && c.z > 0.94) c.z = 0.94;
        if (c.z < 0.02) c.z = 0.02;
      }
    }

    if (localStorage.getItem("carnival.debug")) {
      window.__cp = g;
      g.dropCoin = () => dropCoin();
    }

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const fit = () => {
      const wrap = wrapRef.current;
      if (!wrap) return;
      const maxW = Math.min((wrap.parentElement ? wrap.parentElement.clientWidth : W) * 0.96, 440);
      const availH = Math.max(240, window.innerHeight - wrap.getBoundingClientRect().top - 14);
      const scale = Math.min(maxW / W, availH / H);
      canvas.style.width = W * scale + "px";
      canvas.style.height = H * scale + "px";
      canvas.width = W * dpr * scale;
      canvas.height = H * dpr * scale;
      ctx.setTransform(dpr * scale, 0, 0, dpr * scale, 0, 0);
    };
    fit();
    window.addEventListener("resize", fit);
    const ro = new ResizeObserver(fit);
    ro.observe(document.body);

    /* ---- statisk kabinet, bagt én gang ---- */
    const bg = document.createElement("canvas");
    bg.width = W * 2; bg.height = H * 2;
    {
      const b = bg.getContext("2d");
      b.scale(2, 2);
      // midnatsblåt kabinet
      const cab = b.createLinearGradient(0, 0, 0, H);
      cab.addColorStop(0, "#1a2246");
      cab.addColorStop(0.5, "#131a38");
      cab.addColorStop(1, "#0c1226");
      b.fillStyle = cab; b.fillRect(0, 0, W, H);
      // marquee-top m. møntemblem (ingen tekst)
      const mq = b.createLinearGradient(0, 0, 0, 74);
      mq.addColorStop(0, "#8a5f36"); mq.addColorStop(0.5, "#c9a24b"); mq.addColorStop(1, "#8a5f36");
      b.fillStyle = mq; b.fillRect(8, 6, W - 16, 66);
      // rød marquee-filt m. svage stjerner (mockuppet)
      const mqf = b.createLinearGradient(0, 12, 0, 66);
      mqf.addColorStop(0, "#6b1622"); mqf.addColorStop(1, "#43101a");
      b.fillStyle = mqf; b.fillRect(14, 12, W - 28, 54);
      const star = (sx, sy, s, a, c) => {
        b.fillStyle = c || `rgba(255,225,170,${a})`;
        b.beginPath();
        for (let k = 0; k < 8; k++) {
          const ang = (k / 8) * Math.PI * 2 - Math.PI / 2;
          const rr = k % 2 ? s * 0.38 : s;
          b[k ? "lineTo" : "moveTo"](sx + Math.cos(ang) * rr, sy + Math.sin(ang) * rr);
        }
        b.closePath(); b.fill();
      };
      for (let i = 0; i < 9; i++) {
        const v = Math.sin(i * 73.3) * 913.7, f = v - Math.floor(v);
        const v2 = Math.sin(i * 37.9) * 517.3, f2 = v2 - Math.floor(v2);
        star(22 + f * (W - 44), 18 + f2 * 42, 2.4 + f2 * 2, 0.10 + f * 0.08);
      }
      for (let i = 0; i < 14; i++) {
        const x = 24 + i * (W - 48) / 13;
        b.fillStyle = i % 2 ? "#ffd75e" : "#fff3c8";
        b.shadowColor = "#ffd75e"; b.shadowBlur = 8;
        b.beginPath(); b.arc(x, 12, 3, 0, Math.PI * 2); b.fill();
        b.beginPath(); b.arc(x, 66, 3, 0, Math.PI * 2); b.fill();
        b.shadowBlur = 0;
      }
      // emblem: RIG guldmønt m. teddy (glød + bevel + glint)
      b.shadowColor = "#ffce5e"; b.shadowBlur = 14;
      b.fillStyle = "#c9982e";
      b.beginPath(); b.arc(CX, 39, 21, 0, Math.PI * 2); b.fill();
      b.shadowBlur = 0;
      const em = b.createRadialGradient(CX - 7, 32, 3, CX, 39, 23);
      em.addColorStop(0, "#fff3c2"); em.addColorStop(0.45, "#f0c35a"); em.addColorStop(1, "#b8862a");
      b.fillStyle = em;
      b.beginPath(); b.arc(CX, 39, 20, 0, Math.PI * 2); b.fill();
      b.strokeStyle = "#8a5f1e"; b.lineWidth = 2;
      b.beginPath(); b.arc(CX, 39, 20, 0, Math.PI * 2); b.stroke();
      b.strokeStyle = "rgba(255,243,194,0.7)"; b.lineWidth = 1.4;
      b.beginPath(); b.arc(CX, 39, 16.5, 0, Math.PI * 2); b.stroke();
      // teddy m. bevel
      b.fillStyle = "rgba(255,240,190,0.7)";
      b.beginPath(); b.arc(CX + 0.7, 41.7, 8, 0, Math.PI * 2); b.fill();
      b.beginPath(); b.arc(CX - 5.8, 34.7, 3.6, 0, Math.PI * 2); b.fill();
      b.beginPath(); b.arc(CX + 7.2, 34.7, 3.6, 0, Math.PI * 2); b.fill();
      b.fillStyle = "#8a5f1e";
      b.beginPath(); b.arc(CX, 41, 8, 0, Math.PI * 2); b.fill();
      b.beginPath(); b.arc(CX - 6.5, 34, 3.6, 0, Math.PI * 2); b.fill();
      b.beginPath(); b.arc(CX + 6.5, 34, 3.6, 0, Math.PI * 2); b.fill();
      // glint
      b.fillStyle = "rgba(255,255,255,0.8)";
      b.beginPath(); b.ellipse(CX - 8, 29, 4.5, 2.2, -0.6, 0, Math.PI * 2); b.fill();
      b.lineWidth = 1;
      // sideskinner m. LYSKÆDER (mockuppet: pærer hele vejen ned)
      for (const [x0, bx] of [[0, 13], [W - 26, W - 13]]) {
        const rg = b.createLinearGradient(x0, 0, x0 + 26, 0);
        rg.addColorStop(0, "#5f3a16"); rg.addColorStop(0.5, "#9a6b2e"); rg.addColorStop(1, "#5f3a16");
        b.fillStyle = rg; b.fillRect(x0, 74, 26, 386);
        b.fillStyle = "rgba(90,20,16,0.55)";
        b.fillRect(x0 + (x0 === 0 ? 20 : 0), 74, 6, 386);
        for (let y = 92; y < 456; y += 33) {
          b.fillStyle = (y / 33 | 0) % 2 ? "#ffd75e" : "#fff3c8";
          b.shadowColor = "#ffce5e"; b.shadowBlur = 9;
          b.beginPath(); b.arc(bx, y, 3.4, 0, Math.PI * 2); b.fill();
          b.shadowBlur = 0;
          b.fillStyle = "rgba(255,255,255,0.6)";
          b.beginPath(); b.arc(bx - 1, y - 1, 1, 0, Math.PI * 2); b.fill();
        }
      }
      // blå neon-stave ved dispenser-hjørnerne (mockuppet)
      for (const nx of [30, W - 34]) {
        b.shadowColor = "#4fa8ff"; b.shadowBlur = 12;
        b.fillStyle = "#7cc4ff";
        b.beginPath();
        b.roundRect ? b.roundRect(nx, 108, 4.5, 44, 3) : b.rect(nx, 108, 4.5, 44);
        b.fill();
        b.shadowBlur = 0;
        b.fillStyle = "rgba(255,255,255,0.7)";
        b.fillRect(nx + 1.2, 112, 1.4, 36);
      }
      // dispenser-skinne
      b.fillStyle = "#2a3358"; b.fillRect(40, 86, W - 80, 16);
      b.fillStyle = "rgba(0,0,0,0.5)"; b.fillRect(44, 90, W - 88, 8);
      // bagpanel bag øverste platform: stjerner
      b.fillStyle = "#0d1330"; b.fillRect(26, 104, W - 52, UP.y0 - 104);
      for (let i = 0; i < 26; i++) {
        const v = Math.sin(i * 91.7) * 4310.3, f = v - Math.floor(v);
        const v2 = Math.sin(i * 53.1) * 991.7, f2 = v2 - Math.floor(v2);
        b.fillStyle = `rgba(255,240,200,${0.2 + f2 * 0.4})`;
        b.beginPath(); b.arc(30 + f * (W - 60), 108 + f2 * 38, 0.9 + f * 0.8, 0, Math.PI * 2); b.fill();
      }
      // platform-fladerne: mørk stjerne-filt (statisk → bages her)
      for (const L of [UP, LO]) {
        b.beginPath();
        b.moveTo(CX - halfW(L, 0), yAt(L, 0));
        b.lineTo(CX + halfW(L, 0), yAt(L, 0));
        b.lineTo(CX + halfW(L, 1), yAt(L, 1));
        b.lineTo(CX - halfW(L, 1), yAt(L, 1));
        b.closePath();
        const pg = b.createLinearGradient(0, L.y0, 0, L.y1);
        pg.addColorStop(0, "#1c2a55");
        pg.addColorStop(1, "#2e4278");
        b.fillStyle = pg; b.fill();
        // stjerner i filten
        for (let i = 0; i < 11; i++) {
          const v = Math.sin(i * 57.7 + L.y0) * 761.3, f = v - Math.floor(v);
          const v2 = Math.sin(i * 23.1 + L.y0) * 419.9, f2 = v2 - Math.floor(v2);
          const z = 0.14 + f2 * 0.8;
          star(CX + (f * 2 - 1) * (halfW(L, z) - 12), yAt(L, z), 3 + f * 2.6, 0.05 + f2 * 0.06, `rgba(160,190,255,${0.06 + f2 * 0.07})`);
        }
        // dybdelinjer + kant-skygge bagest
        b.strokeStyle = "rgba(255,255,255,0.045)";
        for (let i = 1; i < 6; i++) {
          const z = i / 6;
          b.beginPath();
          b.moveTo(CX - halfW(L, z), yAt(L, z));
          b.lineTo(CX + halfW(L, z), yAt(L, z));
          b.stroke();
        }
        const sh = b.createLinearGradient(0, L.y0, 0, L.y0 + 16);
        sh.addColorStop(0, "rgba(0,0,0,0.4)"); sh.addColorStop(1, "rgba(0,0,0,0)");
        b.beginPath();
        b.moveTo(CX - halfW(L, 0), yAt(L, 0));
        b.lineTo(CX + halfW(L, 0), yAt(L, 0));
        b.lineTo(CX + halfW(L, 0.2), yAt(L, 0.2));
        b.lineTo(CX - halfW(L, 0.2), yAt(L, 0.2));
        b.closePath();
        b.fillStyle = sh; b.fill();
      }
      // bakke-zone
      b.fillStyle = "#0a0f22"; b.fillRect(26, 396, W - 52, 56);
      const tray = b.createLinearGradient(0, 398, 0, 448);
      tray.addColorStop(0, "rgba(255,215,94,0.25)");
      tray.addColorStop(1, "rgba(255,215,94,0.04)");
      b.fillStyle = tray;
      b.fillRect(CX - TRAY_HW, 398, TRAY_HW * 2, 50);
      b.strokeStyle = "#c9a24b"; b.lineWidth = 2;
      b.strokeRect(CX - TRAY_HW, 398, TRAY_HW * 2, 50);
      // sidegruber = tabt
      b.fillStyle = "rgba(200,53,46,0.16)";
      b.fillRect(28, 398, CX - TRAY_HW - 28, 50);
      b.fillRect(CX + TRAY_HW, 398, W - 28 - (CX + TRAY_HW), 50);
      b.strokeStyle = "rgba(200,53,46,0.5)"; b.lineWidth = 1.5;
      for (const gx of [28, CX + TRAY_HW]) {
        const gw = gx === 28 ? CX - TRAY_HW - 28 : W - 28 - (CX + TRAY_HW);
        b.beginPath();
        b.moveTo(gx + 6, 440); b.lineTo(gx + gw / 2, 408); b.lineTo(gx + gw - 6, 440);
        b.stroke();
      }
      b.lineWidth = 1;
      // HUD: mørkerød filt-plade m. guldramme + SØLV/GULD-plaketter (mockuppet)
      const hp = b.createLinearGradient(0, 460, 0, H);
      hp.addColorStop(0, "#5a1520"); hp.addColorStop(1, "#370b13");
      b.fillStyle = hp; b.fillRect(0, 460, W, H - 460);
      b.fillStyle = "#c9a24b"; b.fillRect(0, 460, W, 2.5);
      b.strokeStyle = "rgba(232,193,90,0.55)"; b.lineWidth = 1.6;
      b.strokeRect(6, 466, W - 12, H - 472);
      b.lineWidth = 1;
      for (const px of [12, W - 104]) {
        b.beginPath();
        b.roundRect ? b.roundRect(px, 470, 92, 62, 9) : b.rect(px, 470, 92, 62);
        const pgK = b.createLinearGradient(0, 470, 0, 532);
        pgK.addColorStop(0, "#1c2a52"); pgK.addColorStop(1, "#111a36");
        b.fillStyle = pgK; b.fill();
        b.strokeStyle = "#c9a24b"; b.lineWidth = 2;
        b.beginPath();
        b.roundRect ? b.roundRect(px, 470, 92, 62, 9) : b.rect(px, 470, 92, 62);
        b.stroke();
        b.lineWidth = 1;
      }
      // metalliske labels: sølv- og guld-gradient m. mørk kontur
      b.font = "800 12px system-ui, sans-serif";
      b.textAlign = "center";
      b.lineWidth = 2.6; b.strokeStyle = "rgba(10,14,30,0.8)";
      const silvG = b.createLinearGradient(0, 474, 0, 488);
      silvG.addColorStop(0, "#ffffff"); silvG.addColorStop(0.5, "#d9e2ea"); silvG.addColorStop(1, "#8fa0ae");
      b.strokeText(t("pu_silver").toUpperCase(), 58, 485);
      b.fillStyle = silvG;
      b.fillText(t("pu_silver").toUpperCase(), 58, 485);
      const goldG = b.createLinearGradient(0, 474, 0, 488);
      goldG.addColorStop(0, "#fff3c2"); goldG.addColorStop(0.5, "#f0c35a"); goldG.addColorStop(1, "#b8862a");
      b.strokeText(t("pu_gold").toUpperCase(), W - 58, 485);
      b.fillStyle = goldG;
      b.fillText(t("pu_gold").toUpperCase(), W - 58, 485);
      b.lineWidth = 1;
      // vignette
      const vg = b.createRadialGradient(CX, H * 0.42, 130, CX, H * 0.46, 420);
      vg.addColorStop(0, "rgba(0,0,0,0)");
      vg.addColorStop(1, "rgba(4,6,16,0.5)");
      b.fillStyle = vg; b.fillRect(0, 0, W, H);
    }

    /* ---- input: ét tap = slip en mønt ---- */
    const dropCoin = () => {
      if (g.over || g.ammo <= 0) return false;
      // max 3 i luften ad gangen
      if (g.falls.filter((f) => f.to === "up").length >= 3) return false;
      g.ammo -= 1;
      g.dropCount += 1;
      g.pressT = g.t;
      setHud({ ammo: g.ammo, gold: g.gold });
      audioRef.current.drop();
      const x = slotX();
      g.falls.push({ x, y0: 96, y1: 0, t: 0, dur: 0.32, gold: false, to: "up" });
      // rytme-bonus: hver 25. mønt drysser 2 ekstra guld i puljen
      if (g.dropCount % 25 === 0) goldRain(2);
      return true;
    };
    const press = (e) => {
      audioRef.current.unlock();
      e.preventDefault();
      dropCoin();
    };
    canvas.addEventListener("mousedown", press);
    canvas.addEventListener("touchstart", press, { passive: false });

    const finish = () => {
      if (g.over) return;
      g.over = true;
      if (g.gold > 0) audioRef.current.fanfare();
      submitPusherScore(g.gold);        // alle scores tæller (fire-and-forget)
      const gold = g.gold;
      setTimeout(() => {
        setResult({ gold });
        setPhase("result");
      }, 600);
    };

    /* ---- step ---- */
    const step = (dt) => {
      g.t += dt;
      const P1 = p1(), P2 = p2();

      // pusher-skub
      for (const c of g.up) if (c.z < P1 + rz(UP)) c.z = P1 + rz(UP);
      for (const c of g.low) if (c.z < P2 + rz(LO)) c.z = P2 + rz(LO);
      relax(UP, g.up);
      relax(LO, g.low);

      // over forkanten? (tårne vælter ud i deres enkeltmønter, let forskudt)
      const spill = (L, list, to) => {
        for (let i = list.length - 1; i >= 0; i--) {
          const c = list[i];
          if (c.z > 1 - rz(L) * 0.4) {
            list.splice(i, 1);
            const coins = c.tower ? c.tower : [c.gold];
            coins.forEach((isGold, k) => {
              const x = c.x + (c.tower ? (Math.random() - 0.5) * 14 : 0);
              const delay = c.tower ? -0.07 * k : 0;
              if (to === "low") {
                g.falls.push({ x, y0: yAt(L, 1), y1: 0, t: delay, dur: 0.3, gold: isGold, to: "low" });
              } else {
                const won = Math.abs(x - CX) <= TRAY_HW - 4;
                g.falls.push({ x, y0: yAt(L, 1), y1: won ? 428 : 444, t: delay, dur: 0.34, gold: isGold, to: won ? "tray" : "lost" });
              }
            });
          }
        }
      };
      spill(UP, g.up, "low");
      spill(LO, g.low, "tray");

      // tærskel-guldregn: løber maskinen tør for guld, fylder den selv på
      if (g.refillCd > 0) g.refillCd -= dt;
      if (!g.over && g.ammo > 0 && g.refillCd <= 0 && goldInMachine() < 4) goldRain(3);
      if (g.banner) { g.banner.t += dt; if (g.banner.t > 1.7) g.banner = null; }

      // faldende mønter (negativ t = forsinket spawn fra tårn-vælt/guldregn)
      for (let i = g.falls.length - 1; i >= 0; i--) {
        const f = g.falls[i];
        f.t += dt;
        if (f.t < f.dur) continue;
        g.falls.splice(i, 1);
        if (f.to === "up") {
          const z = Math.min(Math.max(p1() + rz(UP) + 0.02, 0.06), 0.7);
          const hw = halfW(UP, z) - R;
          g.up.push({ x: Math.min(Math.max(f.x, CX - hw), CX + hw), z, gold: f.gold });
          audioRef.current.thunk();
        } else if (f.to === "low") {
          const z = Math.min(Math.max(p2() + rz(LO) + 0.02 + Math.random() * 0.06, 0.08), 0.6);
          const hw = halfW(LO, z) - R;
          g.low.push({ x: Math.min(Math.max(f.x, CX - hw), CX + hw), z, gold: f.gold });
          audioRef.current.thunk();
        } else if (f.to === "tray") {
          g.quiet = 0;
          if (f.gold) {
            g.gold += 1;
            g.goldFlash = g.t;          // emblemet i toppen blinker
            audioRef.current.gold();
            g.fx.push({ x: f.x, y: 420, txt: "+1", c: "#ffd75e", t: 0 });
          } else {
            g.ammo += 1;
            audioRef.current.silver();
            g.fx.push({ x: f.x, y: 420, txt: "+1", c: "#dfe6ec", t: 0 });
          }
          setHud({ ammo: g.ammo, gold: g.gold });
        } else {
          audioRef.current.lost();
          g.fx.push({ x: f.x, y: 434, txt: "×", c: "#c8352e", t: 0 });
        }
      }

      g.fx.forEach((p) => (p.t += dt));
      g.fx = g.fx.filter((p) => p.t < 1);

      // slut: tom beholdning + stilhed
      if (g.ammo <= 0 && !g.falls.length) {
        g.quiet += dt;
        if (g.quiet > 5) finish();
      } else if (g.ammo > 0) g.quiet = 0;
    };

    /* ---- tegning ---- */
    /* SKINNENDE mønter (ChatGPT-mockup): tyk to-tonet kant, blank flade,
       markant teddy-præg m. bevel og hvid glans-glint */
    const drawCoin = (x, y, gold, ry = 7.4, hl = true) => {
      // kant (tykkelse, to toner) — guldet følger pokalens rige guld (18/7)
      ctx.fillStyle = gold ? "#96660e" : "#5a6570";
      ctx.beginPath(); ctx.ellipse(x, y + 3.4, R, ry, 0, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = gold ? "#e0a92e" : "#aeb9c4";
      ctx.beginPath(); ctx.ellipse(x, y + 1.8, R, ry, 0, 0, Math.PI * 2); ctx.fill();
      // flade
      const gr = ctx.createLinearGradient(x - R, y - ry, x + R * 0.7, y + ry);
      if (gold) { gr.addColorStop(0, "#fff6c4"); gr.addColorStop(0.45, "#ffd254"); gr.addColorStop(1, "#cf9418"); }
      else { gr.addColorStop(0, "#ffffff"); gr.addColorStop(0.45, "#e3eaf1"); gr.addColorStop(1, "#a8b4c0"); }
      ctx.fillStyle = gr;
      ctx.beginPath(); ctx.ellipse(x, y, R, ry, 0, 0, Math.PI * 2); ctx.fill();
      // møntring
      ctx.strokeStyle = gold ? "rgba(140,92,16,0.65)" : "rgba(88,102,114,0.55)";
      ctx.lineWidth = 1.6;
      ctx.beginPath(); ctx.ellipse(x, y, R - 2.6, ry - 1.9, 0, 0, Math.PI * 2); ctx.stroke();
      ctx.lineWidth = 1;
      // teddy-præg m. bevel (lys kant over, mørk form under)
      const head = (dx2, dy2, c) => {
        ctx.fillStyle = c;
        ctx.beginPath(); ctx.ellipse(x + dx2, y + 1 + dy2, 4.2, 3.1, 0, 0, Math.PI * 2); ctx.fill();
        ctx.beginPath(); ctx.ellipse(x - 3.3 + dx2, y - 2 + dy2, 1.8, 1.4, 0, 0, Math.PI * 2); ctx.fill();
        ctx.beginPath(); ctx.ellipse(x + 3.3 + dx2, y - 2 + dy2, 1.8, 1.4, 0, 0, Math.PI * 2); ctx.fill();
      };
      head(0.4, 0.5, gold ? "rgba(255,246,196,0.8)" : "rgba(255,255,255,0.85)");
      head(0, 0, gold ? "rgba(140,92,16,0.75)" : "rgba(80,94,106,0.7)");
      if (hl) {
        // glans: bue + lille glint (metal-look = hård, lys refleks)
        ctx.strokeStyle = "rgba(255,255,255,0.85)";
        ctx.lineWidth = 1.8;
        ctx.beginPath(); ctx.ellipse(x - 1.5, y - 1.5, R - 4.5, ry - 3.6, 0, Math.PI * 1.02, Math.PI * 1.62); ctx.stroke();
        ctx.lineWidth = 1;
        ctx.fillStyle = "rgba(255,255,255,0.95)";
        ctx.beginPath(); ctx.arc(x + R * 0.42, y - ry * 0.42, 1.2, 0, Math.PI * 2); ctx.fill();
      }
    };

    const platTrapez = (L, z0, z1) => {
      ctx.beginPath();
      ctx.moveTo(CX - halfW(L, z0), yAt(L, z0));
      ctx.lineTo(CX + halfW(L, z0), yAt(L, z0));
      ctx.lineTo(CX + halfW(L, z1), yAt(L, z1));
      ctx.lineTo(CX - halfW(L, z1), yAt(L, z1));
      ctx.closePath();
    };

    const drawLayer = (L, list, P) => {
      // platform-fladen m. stjerne-filt er BAGT i bg — her kun det bevægelige
      // pusher-blok (dækker z 0..P): blank rød bar m. guldkant som mockuppet
      platTrapez(L, 0, P);
      const bk = ctx.createLinearGradient(0, L.y0, 0, yAt(L, P));
      bk.addColorStop(0, "#7e1c16");
      bk.addColorStop(0.6, "#b93028");
      bk.addColorStop(1, "#d24a3c");
      ctx.fillStyle = bk; ctx.fill();
      // gloss-stribe hen over blokken
      platTrapez(L, Math.max(0, P * 0.25), Math.min(P, P * 0.5));
      ctx.fillStyle = "rgba(255,255,255,0.12)"; ctx.fill();
      // pusher-front (guld + lys kant)
      ctx.fillStyle = "#e8c15a";
      ctx.fillRect(CX - halfW(L, P), yAt(L, P) - 3.4, halfW(L, P) * 2, 2);
      ctx.fillStyle = "#f7e3a0";
      ctx.fillRect(CX - halfW(L, P), yAt(L, P) - 3.4, halfW(L, P) * 2, 0.9);
      ctx.fillStyle = "rgba(60,10,8,0.65)";
      ctx.fillRect(CX - halfW(L, P), yAt(L, P) - 1.2, halfW(L, P) * 2, 2.6);
      // mønter (bagerste først); tårne tegnes som stakke m. 4,6 px pr. lag
      [...list].sort((a, b) => a.z - b.z).forEach((c) => {
        if (c.tower) {
          const y = yAt(L, c.z);
          c.tower.forEach((isGold, k) => drawCoin(c.x, y - k * 4.6, isGold, 7.4, k === c.tower.length - 1));
        } else {
          drawCoin(c.x, yAt(L, c.z), c.gold);
        }
      });
      // forkant
      ctx.fillStyle = "rgba(6,10,24,0.85)";
      ctx.fillRect(CX - halfW(L, 1), L.y1, halfW(L, 1) * 2, 7);
      ctx.fillStyle = "rgba(201,162,75,0.55)";
      ctx.fillRect(CX - halfW(L, 1), L.y1, halfW(L, 1) * 2, 1.6);
    };

    const draw = () => {
      ctx.drawImage(bg, 0, 0, W, H);
      // dispenser-slids
      const sx = slotX();
      ctx.fillStyle = "#c9a24b";
      ctx.beginPath();
      ctx.moveTo(sx - 9, 88); ctx.lineTo(sx + 9, 88); ctx.lineTo(sx + 5, 102); ctx.lineTo(sx - 5, 102);
      ctx.closePath(); ctx.fill();
      ctx.fillStyle = "#1a2246"; ctx.fillRect(sx - 4, 90, 8, 10);
      if (!g.over && g.ammo > 0) {
        ctx.fillStyle = "rgba(255,215,94,0.8)";
        ctx.beginPath(); ctx.moveTo(sx - 5, 108); ctx.lineTo(sx + 5, 108); ctx.lineTo(sx, 116); ctx.closePath(); ctx.fill();
      }
      drawLayer(UP, g.up, p1());
      drawLayer(LO, g.low, p2());
      // faldende mønter (t<0 = venter på sit spawn i tårn-/guldregn-kaskaden)
      for (const f of g.falls) {
        if (f.t < 0) continue;
        const k = Math.min(f.t / f.dur, 1);
        let y1 = f.y1;
        if (f.to === "up") y1 = yAt(UP, Math.min(Math.max(p1() + rz(UP) + 0.02, 0.06), 0.7));
        if (f.to === "low") y1 = yAt(LO, 0.14);
        const y = f.y0 + (y1 - f.y0) * (k * k);
        const squash = 4 + Math.abs(Math.cos(f.t * 16)) * 3.4;   // spinner i luften
        ctx.globalAlpha = f.to === "lost" ? 1 - k * 0.6 : 1;
        drawCoin(f.x, y, f.gold, squash, false);
        ctx.globalAlpha = 1;
      }
      // svævetekster
      for (const p of g.fx) {
        ctx.globalAlpha = 1 - p.t;
        ctx.font = "bold 17px 'Alfa Slab One', Georgia, serif";
        ctx.textAlign = "center";
        ctx.lineWidth = 4; ctx.strokeStyle = "rgba(6,10,20,0.8)";
        ctx.strokeText(p.txt, p.x, p.y - p.t * 26);
        ctx.fillStyle = p.c;
        ctx.fillText(p.txt, p.x, p.y - p.t * 26);
        ctx.globalAlpha = 1; ctx.lineWidth = 1;
      }
      // GULDREGN-banner (pop-ind-skalering, guld m. mørk kontur)
      if (g.banner) {
        const k = g.banner.t;
        const sc = k < 0.18 ? k / 0.18 : 1;
        ctx.save();
        ctx.translate(CX, 208);
        ctx.scale(sc, sc);
        ctx.globalAlpha = Math.min(1, (1.7 - k) * 2);
        ctx.font = "bold 30px 'Alfa Slab One', Georgia, serif";
        ctx.textAlign = "center";
        ctx.lineWidth = 6; ctx.strokeStyle = "rgba(20,12,4,0.85)";
        ctx.strokeText(g.banner.txt, 0, 0);
        ctx.fillStyle = "#ffd75e";
        ctx.fillText(g.banner.txt, 0, 0);
        ctx.restore();
        ctx.globalAlpha = 1; ctx.lineWidth = 1;
      }
      // glasrefleks
      ctx.fillStyle = "rgba(255,255,255,0.045)";
      ctx.beginPath();
      ctx.moveTo(48, 76); ctx.lineTo(96, 76); ctx.lineTo(40, 456); ctx.lineTo(28, 456);
      ctx.closePath(); ctx.fill();
      ctx.beginPath();
      ctx.moveTo(120, 76); ctx.lineTo(138, 76); ctx.lineTo(76, 456); ctx.lineTo(60, 456);
      ctx.closePath(); ctx.fill();
      // HUD: tællere i plaketterne + stor blå release-knap m. teddy-silhuet
      ctx.textAlign = "left";
      drawCoin(32, 510, false, 6.6);
      ctx.font = "800 20px 'Alfa Slab One', Georgia, serif";
      ctx.fillStyle = "#F6EED9";
      ctx.fillText(String(g.ammo), 50, 518);
      ctx.textAlign = "right";
      ctx.fillStyle = "#ffd75e";
      ctx.fillText(String(g.gold), W - 50, 518);
      drawCoin(W - 32, 510, true, 6.6);
      // hint/status: to linjer OPPE I MØNTBOKSEN (bakken)
      ctx.textAlign = "center";
      ctx.font = "600 11px Georgia, serif";
      const msg = g.ammo <= 0 && !g.over ? t("pu_last")
        : g.ammo >= AMMO_START - 2 ? t("pu_hint") : null;
      if (msg) {
        const wds = msg.split(" ");
        const half = Math.ceil(wds.length / 2);
        ctx.lineWidth = 3; ctx.strokeStyle = "rgba(6,9,20,0.85)";
        ctx.strokeText(wds.slice(0, half).join(" "), CX, 417, 170);
        ctx.strokeText(wds.slice(half).join(" "), CX, 431, 170);
        ctx.fillStyle = "rgba(246,238,217,0.9)";
        ctx.fillText(wds.slice(0, half).join(" "), CX, 417, 170);
        ctx.fillText(wds.slice(half).join(" "), CX, 431, 170);
        ctx.lineWidth = 1;
      }
      // release-knappen i panelets CENTER (tryk-anim via g.pressT)
      const press = g.pressT && g.t - g.pressT < 0.12 ? 0.92 : 1;
      ctx.save();
      ctx.translate(CX, 500);
      ctx.scale(press, press);
      ctx.fillStyle = "rgba(0,0,0,0.4)";
      ctx.beginPath(); ctx.ellipse(0, 4, 27, 25, 0, 0, Math.PI * 2); ctx.fill();
      const ring = ctx.createLinearGradient(0, -26, 0, 26);
      ring.addColorStop(0, "#f0d27a"); ring.addColorStop(1, "#a87914");
      ctx.fillStyle = ring;
      ctx.beginPath(); ctx.arc(0, 0, 26, 0, Math.PI * 2); ctx.fill();
      const btn = ctx.createRadialGradient(-6, -8, 3, 0, 0, 23);
      btn.addColorStop(0, "#8fd0f2"); btn.addColorStop(0.5, "#3f8ccc"); btn.addColorStop(1, "#1d4f8a");
      ctx.fillStyle = btn;
      ctx.beginPath(); ctx.arc(0, 0, 21.5, 0, Math.PI * 2); ctx.fill();
      // teddy-silhuet
      ctx.fillStyle = "rgba(16,42,80,0.85)";
      ctx.beginPath(); ctx.arc(0, 1.5, 8.4, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.arc(-6.7, -5.6, 3.5, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.arc(6.7, -5.6, 3.5, 0, Math.PI * 2); ctx.fill();
      // glans
      ctx.fillStyle = "rgba(255,255,255,0.45)";
      ctx.beginPath(); ctx.ellipse(-5.5, -9.5, 9.5, 5, -0.5, 0, Math.PI * 2); ctx.fill();
      ctx.restore();
      // emblem-BLINK: guldmønten i toppen pulserer, når guld lander i boksen
      if (g.goldFlash !== undefined && g.t - g.goldFlash < 0.9) {
        const k = 1 - (g.t - g.goldFlash) / 0.9;
        const pulse = 1 + Math.sin((g.t - g.goldFlash) * 18) * 0.06 * k;
        ctx.save();
        ctx.translate(CX, 39);
        ctx.scale(pulse, pulse);
        ctx.globalAlpha = 0.55 * k;
        const fl = ctx.createRadialGradient(0, 0, 4, 0, 0, 30);
        fl.addColorStop(0, "rgba(255,248,214,0.95)");
        fl.addColorStop(0.55, "rgba(255,215,94,0.55)");
        fl.addColorStop(1, "rgba(255,215,94,0)");
        ctx.fillStyle = fl;
        ctx.beginPath(); ctx.arc(0, 0, 30, 0, Math.PI * 2); ctx.fill();
        ctx.globalAlpha = 0.9 * k;
        ctx.strokeStyle = "#fff3c2"; ctx.lineWidth = 2.4;
        ctx.beginPath(); ctx.arc(0, 0, 22.5, 0, Math.PI * 2); ctx.stroke();
        // fire små glimt-stjerner
        for (let s = 0; s < 4; s++) {
          const a = s * Math.PI / 2 + Math.PI / 4;
          const sx = Math.cos(a) * 27, sy = Math.sin(a) * 27;
          ctx.fillStyle = "#fff3c2";
          ctx.beginPath();
          ctx.moveTo(sx, sy - 4 * k); ctx.lineTo(sx + 1.6 * k, sy);
          ctx.lineTo(sx, sy + 4 * k); ctx.lineTo(sx - 1.6 * k, sy);
          ctx.closePath(); ctx.fill();
        }
        ctx.restore();
        ctx.globalAlpha = 1; ctx.lineWidth = 1;
      }
    };

    let raf, last = performance.now();
    const loop = (ts) => {
      const dt = Math.min((ts - last) / 1000, 0.033);
      last = ts;
      step(dt);
      draw();
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", fit);
      ro.disconnect();
      canvas.removeEventListener("mousedown", press);
      canvas.removeEventListener("touchstart", press);
    };
  }, [phase]);

  /* ================= UI ================= */
  return (
    <div className={"pu-root" + (phase !== "setup" ? " ingame" : "")}>
      <style>{css}</style>

      <header className="booth">
        <button className="back" onClick={onExit}>{t("back")}</button>
        <h1>{t("tile_pusher").toUpperCase()}</h1>
        <div className="signs">
          <span className="sign cream">{t("sign_free")}</span>
          <span className="sign red">{t("pu_sign2")}</span>
        </div>
      </header>

      {phase === "setup" && (
        <main className="panel">
          <p className="lede">{t("pu_lede")}</p>
          <ul className="rules">
            <li>{t("pu_rule1")}</li>
            <li>{t("pu_rule2")}</li>
            <li>{t("pu_rule3")}</li>
          </ul>
          <button className="cta" onClick={startGame}>{t("pu_start")}</button>
          <button className="cta hs" onClick={openHs}>{t("pb_hs_btn")}</button>
        </main>
      )}

      {phase !== "setup" && (
        <>
          <div className="scorebar">
            <span>{t("pu_silver")}: {hud.ammo}</span>
            <span className="goldc">{t("pu_gold")}: {hud.gold}</span>
          </div>
          <div ref={wrapRef} className="boardwrap">
            <canvas ref={canvasRef} />
          </div>
        </>
      )}

      {phase === "result" && result && (
        <div className="overlay">
          <div className="card">
            <div className={"ribbon" + (result.gold >= 8 ? "" : " dark")}>
              {result.gold === 1 ? t("pu_res_title1") : t("pu_res_title", { n: result.gold })}
            </div>
            <p className="pu-total">{result.gold}<span> {t("pu_gold").toLowerCase()}</span></p>
            <p className="pu-note">{t("pu_res_note")}</p>
            <button className="cta hs" onClick={openHs}>{t("pb_hs_btn")}</button>
            <button className="cta" onClick={startGame}>{t("play_again")}</button>
            <button className="cta alt" onClick={onExit}>{t("to_menu")}</button>
          </div>
        </div>
      )}

      {hsOpen && (() => {
        const list = hsData ? (hsTab === "month" ? hsData.month : hsData.alltime) : [];
        return (
          <div className="overlay hsov" onClick={(e) => { if (e.target === e.currentTarget) setHsOpen(false); }}>
            <div className="card hscard">
              <div className="ribbon">{t("pb_hs_btn")}</div>
              <div className="hstabs">
                <button className={hsTab === "month" ? "on" : ""} onClick={() => setHsTab("month")}>{t("pb_hs_month")}</button>
                <button className={hsTab === "all" ? "on" : ""} onClick={() => setHsTab("all")}>{t("pb_hs_all")}</button>
              </div>
              {!hsData && !hsErr && <p className="hsmsg">· · ·</p>}
              {hsErr && <p className="hsmsg">{t("pb_hs_err")}</p>}
              {hsData && (list.length ? (
                <div className="hslist">
                  {list.map((e, i) => (
                    <div key={e.dev + i} className={"hsrow" + (e.me ? " me" : "")}>
                      <span className="rk">{i + 1}</span>
                      <span className="nm">{e.name}<em>{fmtDate(e.ts, hsTab === "all")}</em></span>
                      <b>{e.score}</b>
                    </div>
                  ))}
                </div>
              ) : <p className="hsmsg">{t("pb_hs_empty")}</p>)}
              <button className="cta alt" onClick={() => setHsOpen(false)}>{t("pb_hs_close")}</button>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

const css = `
.pu-root{
  --cream:#F6EED9; --ink:#22301c; --red:#C8352E;
  min-height:100vh; background:#0c1226;
  background-image:radial-gradient(circle at 50% -10%, #263465 0%, #0c1226 60%);
  font-family:'Nunito',system-ui,sans-serif; color:var(--cream);
  display:flex; flex-direction:column; align-items:center; padding-bottom:24px;
  -webkit-user-select:none; user-select:none; touch-action:manipulation;
}
.booth{width:100%; max-width:520px; text-align:center; padding:10px 12px 6px; position:relative;}
.back{
  position:absolute; left:10px; top:12px; z-index:2;
  background:var(--cream); color:var(--ink); border:none; border-radius:8px;
  font-family:inherit; font-weight:800; font-size:13px; padding:7px 11px; cursor:pointer;
  box-shadow:0 3px 0 #b3a880;
}
.booth h1{
  font-family:'Alfa Slab One',Georgia,serif; font-size:clamp(22px,6.4vw,38px);
  margin:0; letter-spacing:2px; color:var(--cream);
  text-shadow:0 3px 0 #232c56, 0 6px 12px rgba(0,0,0,.5);
}
.signs{display:flex; gap:8px; justify-content:center; margin-top:6px; flex-wrap:wrap;}
.sign{font-weight:800; font-size:12px; padding:4px 10px; border-radius:3px; letter-spacing:.5px;}
.sign.cream{background:var(--cream); color:var(--ink); border:1px solid #c9bf9f;}
.sign.red{background:var(--red); color:#fff;}
.ingame .signs{display:none;}
.ingame .booth{padding:8px 12px 2px;}
.ingame .booth h1{font-size:clamp(20px,5vw,28px);}
.ingame .back{top:6px; padding:5px 9px;}
.panel{
  width:min(92%,440px); background:var(--cream); color:var(--ink);
  border-radius:12px; padding:18px 20px 22px; margin-top:14px;
  box-shadow:0 10px 30px rgba(0,0,0,.4);
}
.lede{margin:0 0 12px; font-size:15px; line-height:1.45;}
.rules{list-style:none; margin:0; padding:0;}
.rules li{font-weight:800; font-size:14px; padding:6px 0; border-bottom:1px dashed #cbbf9c;}
.cta{
  width:100%; margin-top:14px; padding:14px; font-size:19px; font-weight:800; cursor:pointer;
  font-family:'Alfa Slab One',Georgia,serif; letter-spacing:1px;
  background:var(--red); color:#fff; border:none; border-radius:10px;
  box-shadow:0 4px 0 #7e1f1a;
}
.cta:active{transform:translateY(3px); box-shadow:0 1px 0 #7e1f1a;}
.cta.alt{margin-top:10px; background:#3d497a; box-shadow:0 4px 0 #222b4d; font-size:15px; padding:11px;}
.cta.alt:active{box-shadow:0 1px 0 #222b4d;}
.cta.hs{
  background:linear-gradient(180deg,#7a4e2c,#5f3d1f); color:#ffe9a8;
  border:1.5px solid #c9a24b; box-shadow:0 4px 0 #3f2812; font-size:15px; padding:11px;
}
.cta.hs:active{box-shadow:0 1px 0 #3f2812;}
.scorebar{
  width:min(96%,520px); display:flex; justify-content:space-between; align-items:center;
  gap:8px; font-size:14px; font-weight:800; padding:6px 4px 4px;
}
.scorebar .goldc{color:#ffd75e;}
.boardwrap{
  width:auto; max-width:96%; border-radius:10px; overflow:hidden;
  border:4px solid #2a3358; box-shadow:0 12px 30px rgba(0,0,0,.55);
}
.boardwrap canvas{display:block; touch-action:none;}
.overlay{
  position:fixed; inset:0; background:rgba(6,9,20,.8); z-index:10;
  display:flex; align-items:center; justify-content:center;
}
.card{
  width:min(88%,380px); background:var(--cream); color:var(--ink);
  border-radius:14px; padding:0 22px 22px; text-align:center;
  box-shadow:0 18px 50px rgba(0,0,0,.6);
}
.ribbon{
  font-family:'Alfa Slab One',Georgia,serif; font-size:20px; letter-spacing:1px;
  background:var(--red); color:#fff; margin:0 -22px 14px; padding:14px;
  border-radius:14px 14px 0 0;
}
.ribbon.dark{background:#3d497a;}
.pu-total{
  font-family:'Alfa Slab One',Georgia,serif; font-size:46px; margin:2px 0 4px; color:#a87914;
}
.pu-total span{font-size:16px; color:#8a7a55;}
.pu-note{font-weight:800; font-size:13px; margin:0 0 10px; opacity:.75;}
.hsov{z-index:12;}
.hscard{max-height:86vh; overflow-y:auto;}
.hstabs{display:flex; gap:8px; margin:0 0 10px;}
.hstabs button{
  flex:1; padding:9px 6px; font-family:inherit; font-weight:800; font-size:13.5px;
  border-radius:8px; cursor:pointer; border:1.5px solid #cbbf9c; background:#efe6cd; color:#6b5c38;
}
.hstabs button.on{background:linear-gradient(180deg,#f7df9a,#e9b94e); border-color:#a87914; color:#4a2c00;}
.hslist{margin:0 0 6px;}
.hsrow{
  display:flex; align-items:center; gap:10px; font-weight:800; font-size:14px;
  padding:6px 8px; border-bottom:1px dashed #cbbf9c; border-radius:6px;
}
.hsrow:last-child{border-bottom:none;}
.hsrow .rk{width:20px; text-align:right; color:#8a7a55;}
.hsrow .nm{flex:1; text-align:left; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;}
.hsrow .nm em{font-style:normal; font-weight:700; font-size:11px; color:#8a7a55; margin-left:8px;}
.hsrow b{color:#7e1f1a; font-size:15px;}
.hsrow.me{background:linear-gradient(180deg,#f9ecc4,#f2dc9a); box-shadow:inset 0 0 0 1.5px #c9a24b;}
.hsmsg{font-weight:800; font-size:14px; color:#8a7a55; padding:14px 0;}
`;
